import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, Crown, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function VisitsPage() {
  const { user, api } = useAuth();
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVisitors();
    // Mark visits as read when page is visited
    markVisitsAsRead();
  }, []);

  const fetchVisitors = async () => {
    setLoading(true);
    try {
      const response = await api.get('/visits');
      setVisitors(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // User not subscribed - handled in UI
      } else {
        toast.error('Erreur lors du chargement des visites');
      }
    } finally {
      setLoading(false);
    }
  };

  const markVisitsAsRead = async () => {
    try {
      await api.post('/visits/mark-read');
    } catch (error) {
      // Silently fail
    }
  };

  // If user is not subscribed, show upgrade message
  if (!user?.is_subscribed) {
    return (
      <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="visits-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="glass-card p-12">
            <Crown className="w-16 h-16 text-gold mx-auto mb-6" />
            <h1 className="font-serif text-3xl gold-gradient-text mb-4">
              Abonnez-vous
            </h1>
            <p className="text-gray-400 mb-2">
              Découvrez qui a visité votre profil et ne manquez aucune opportunité !
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Photos, messagerie, chat en direct, recherche avancée...
            </p>
            <Link to="/subscription" className="btn-gold inline-block" data-testid="upgrade-btn">
              Découvrir les offres
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="visits-page">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl gold-gradient-text flex items-center gap-3">
              <Eye className="w-8 h-8" />
              Mes visites
            </h1>
            <p className="text-gray-500 mt-1">
              {visitors.length} profil{visitors.length !== 1 ? 's' : ''} {visitors.length !== 1 ? 'ont' : 'a'} visité votre profil
            </p>
          </div>
          <Button 
            onClick={fetchVisitors} 
            className="btn-outline-gold flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Visitors Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          </div>
        ) : visitors.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Eye className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-2">Aucune visite pour le moment</p>
            <p className="text-gray-500 text-sm">
              Complétez votre profil et ajoutez des photos pour attirer plus de visiteurs !
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="visitors-grid">
            {visitors.map((visitor) => (
              <VisitorCard key={visitor.user_id} visitor={visitor} />
            ))}
          </div>
        )}

        {/* Info box */}
        {visitors.length > 0 && (
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Les 50 dernières visites sont affichées. Une seule visite par profil.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const VisitorCard = ({ visitor }) => {
  // Gender symbol based on profile type
  const getGenderSymbol = (type) => {
    switch (type) {
      case 'femme': return '♀';
      case 'homme': return '♂';
      case 'couple': return '♂♀';
      default: return '♂';
    }
  };

  // Gender color
  const getGenderColor = (type) => {
    switch (type) {
      case 'femme': return 'text-pink-400';
      case 'homme': return 'text-blue-400';
      case 'couple': return 'text-purple-400';
      default: return 'text-blue-400';
    }
  };

  return (
    <Link to={`/member/${visitor.user_id}`}>
      <div 
        className="glass-card p-4 hover:border-gold/50 transition-all hover:bg-white/5 group rounded-lg aspect-square flex flex-col items-center justify-center"
        data-testid="visitor-card"
      >
        {/* Avatar / Initial */}
        <div className="flex-1 flex items-center justify-center">
          {visitor.profile_picture ? (
            <img 
              src={getImageUrl(visitor.profile_picture)}
              alt={visitor.pseudo}
              className="w-16 h-16 rounded-full object-cover border-2 border-gold/30 group-hover:border-gold transition-colors"
            />
          ) : (
            <span className="text-5xl font-serif text-gold group-hover:text-gold-light transition-colors">
              {visitor.pseudo?.[0]?.toUpperCase() || '?'}
            </span>
          )}
        </div>

        {/* Info */}
        <div className="text-center mt-3 w-full">
          <h3 className="font-semibold text-gold group-hover:text-gold-light transition-colors truncate">
            {visitor.pseudo}
          </h3>
          <p className={`text-sm ${getGenderColor(visitor.profile_type)}`}>
            {getGenderSymbol(visitor.profile_type)} {visitor.profile_type?.charAt(0).toUpperCase() + visitor.profile_type?.slice(1)} · {visitor.age}a
          </p>
        </div>
      </div>
    </Link>
  );
};
